import { useCallback, useMemo, useState } from "react";
import {
  CampaignTimelineStage,
  DeliverableTimelineMap,
  RangePreset,
} from "../types";
import {
  addDays,
  daysBetween,
  parseISODate,
  RANGE_PRESETS,
  startOfDay,
  toISODate,
} from "../utils/dateUtils";
import {
  applyStageDurations,

  spreadStagesEvenly,
} from "../utils/stageUtils";

interface UseGanttTimelineProps {
  campaignStartDate?: string;
  campaignEndDate?: string;
  deliverableNames: string[];
  timelineByDeliverable?: DeliverableTimelineMap;
  onChangeDeliverableTimeline?: (
    deliverableName: string,
    next: CampaignTimelineStage[]
  ) => void;
  onApplyTimelineToAll?: (next: CampaignTimelineStage[]) => void;
}

export function useGanttTimeline({
  campaignStartDate,
  campaignEndDate,
  deliverableNames,
  timelineByDeliverable,
  onChangeDeliverableTimeline,
  onApplyTimelineToAll,
}: UseGanttTimelineProps) {
  const today = useMemo(() => startOfDay(new Date()), []);

  const [rangeStartISO, setRangeStartISO] = useState(
    () => campaignStartDate || toISODate(addDays(today, -7))
  );
  const [rangeEndISO, setRangeEndISO] = useState(
    () => campaignEndDate || toISODate(addDays(today, 35))
  );
  const [userAdjusted, setUserAdjusted] = useState(false);

  const rangeStart = useMemo(
    () => parseISODate(rangeStartISO) || addDays(today, -7),
    [rangeStartISO, today]
  );
  const rangeEnd = useMemo(
    () => parseISODate(rangeEndISO) || addDays(today, 35),
    [rangeEndISO, today]
  );
  const totalDays = useMemo(
    () =>
      Math.max(
        daysBetween(
          rangeStart,
          rangeEnd < rangeStart ? rangeStart : rangeEnd
        ) + 1,
        1
      ),
    [rangeStart, rangeEnd]
  );

  const { ticks, todayPct } = useMemo(() => {
    const tickCount = Math.min(8, Math.max(4, Math.ceil(totalDays / 7) + 1));
    const step = (totalDays - 1) / Math.max(tickCount - 1, 1);
    const tickDates = Array.from({ length: tickCount }, (_, i) =>
      addDays(rangeStart, Math.round(i * step))
    );
    const todayClamped = Math.min(
      Math.max(0, daysBetween(rangeStart, today)),
      totalDays - 1
    );
    return {
      ticks: tickDates,
      todayPct: (todayClamped / totalDays) * 100,
    };
  }, [rangeStart, totalDays, today]);

  const applyEqualDistribution = useCallback(
    (start: Date, end: Date) => {
      if (onChangeDeliverableTimeline) {
        deliverableNames.forEach((name) => {
          const current =
            timelineByDeliverable?.[name] && timelineByDeliverable[name].length > 0
              ? timelineByDeliverable[name]
              : [];
          onChangeDeliverableTimeline(name, spreadStagesEvenly(current, start, end));
        });
        return;
      }
      if (onApplyTimelineToAll && deliverableNames[0]) {
        const current =
          timelineByDeliverable?.[deliverableNames[0]] &&
          timelineByDeliverable[deliverableNames[0]].length > 0
            ? timelineByDeliverable[deliverableNames[0]]
            : [];
        onApplyTimelineToAll(spreadStagesEvenly(current, start, end));
      }
    },
    [deliverableNames, onApplyTimelineToAll, onChangeDeliverableTimeline, timelineByDeliverable]
  );

  const handleRangeStartChange = (value: string) => {
    if (!value) return;
    setRangeStartISO(value);
    const start = parseISODate(value);
    const end = parseISODate(rangeEndISO);
    // if (start && end && end < start) {
    //   ToastService.error("End date cannot be less than start date");
    // }
  };

  const handleRangeEndChange = (value: string) => {
    if (!value) return;
    setRangeEndISO(value);
    const end = parseISODate(value);
    const start = parseISODate(rangeStartISO);
    // if (start && end && end < start) {
    //   ToastService.error("End date cannot be less than start date");
    // }
  };

  const applyRange = (start: Date, end: Date) => {
    setRangeStartISO(toISODate(start));
    setRangeEndISO(toISODate(end));
  };

  const applyPreset = (preset: RangePreset) => {
    const { start, end } = preset.build();
    applyRange(start, end);
  };

  const redistribute = () => {
    if (rangeEnd < rangeStart) {
      // ToastService.error("End date cannot be less than start date");
      return;
    }
    setUserAdjusted(false);
    applyEqualDistribution(rangeStart, rangeEnd);
  };

  const applyStageDurationsToTimeline = useCallback(
    (durations: Record<string, number>, start: Date = rangeStart) => {
      if (onChangeDeliverableTimeline) {
        deliverableNames.forEach((name) => {
          const current =
            timelineByDeliverable?.[name] && timelineByDeliverable[name].length > 0
              ? timelineByDeliverable[name]
              : [];
          onChangeDeliverableTimeline(name, applyStageDurations(current, start, durations));
        });
        return;
      }
      if (onApplyTimelineToAll && deliverableNames[0]) {
        const current =
          timelineByDeliverable?.[deliverableNames[0]] &&
          timelineByDeliverable[deliverableNames[0]].length > 0
            ? timelineByDeliverable[deliverableNames[0]]
            : [];
        onApplyTimelineToAll(applyStageDurations(current, start, durations));
      }
    },
    [deliverableNames, onApplyTimelineToAll, onChangeDeliverableTimeline, rangeStart, timelineByDeliverable]
  );

  return {
    today,
    rangeStart,
    rangeEnd,
    totalDays,
    ticks,
    todayPct,
    rangePresets: RANGE_PRESETS,
    handleRangeStartChange,
    handleRangeEndChange,
    applyPreset,
    redistribute,
    applyStageDurationsToTimeline,
    setUserAdjusted,
  };
}
